import React, {
	useMemo,
	type JSXElementConstructor,
	type MutableRefObject,
	type ReactNode,
} from 'react';
import { Nav4ITAM } from '@jira/itam__nav4-menu-item/src/Nav4ITAM.tsx';
import { isTeamsAppEnabled } from '@jira/platform__teams-app-config/src/index.tsx';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import type { ItemsConfig } from '@atlassian/global-side-navigation/common/types';
import type {
	CustomItemConfig,
	CustomItemProps,
	NavItemConfig,
} from '@atlassian/navigation-common/types';
import { Nav4Filters } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-filters/src/index.tsx';
import {
	L1_MENU_ID,
	type JiraConfigurableNavigationItem,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { Nav4Apps } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { Customers } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-customers/src/index.tsx';
import { CustomerService } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Nav4Dashboards } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Operations } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Plans } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Nav4Roadmaps } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Nav4RoadmapsNew } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/Nav4RoadmapsNew.tsx';
import { Teams } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { Nav4RoadmapsNewWithRecent } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/Nav4RoadmapsNewWithRecent.tsx';

import { getConfigurableMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { isConfigurableMenuId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/is-configurable-menu-id/index.tsx';
import { useIntl } from '@atlassian/jira-intl';

import type { GSNForContentSidebarEntryPoint$data } from './__generated__/GSNForContentSidebarEntryPoint.graphql.ts';

type ComponentProps = {
	queryRef: GSNForContentSidebarEntryPoint$data;
	isExpanded: boolean;
};

export function useItemsConfig(
	navItems: JiraConfigurableNavigationItem[],
	queryRef: GSNForContentSidebarEntryPoint$data,
): ItemsConfig {
	const { formatMessage } = useIntl();

	return useMemo(() => {
		const componentProps: ComponentProps = { isExpanded: false, queryRef };

		return navItems.reduce<ItemsConfig>((acc, { menuId }) => {
			// We need a configurable menu id and a component to continue
			if (isConfigurableMenuId(menuId)) {
				const { message, Icon } = getConfigurableMessageAndIcon(menuId);
				const label = formatMessage(message);
				// See src/packages/navigation-apps/sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx
				// Icon is decorative in the customization modal — the adjacent text label provides context.
				const elemBefore = fg('ai-a11y-111-29329') ? (
					<Icon label="" spacing="spacious" color="currentColor" />
				) : (
					<Icon label={label} spacing="spacious" color="currentColor" />
				);
				const itemConfig = mapMenuIdToNavItemConfig({
					componentProps,
					elemBefore,
					label,
					menuId,
				});

				if (itemConfig) {
					acc[menuId] = itemConfig;
				}
			}

			return acc;
		}, {});
	}, [formatMessage, navItems, queryRef]);
}

interface mapMenuIdToComponentParams {
	componentProps: ComponentProps;
	elemBefore: ReactNode;
	label: string;
	menuId: string;
}

function mapMenuIdToNavItemConfig(params: mapMenuIdToComponentParams): NavItemConfig | null {
	const { componentProps, elemBefore, label, menuId } = params;

	switch (menuId) {
		case L1_MENU_ID.APPS:
			return buildCustomComponentConfig({
				Component: Nav4Apps,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.ASSETS:
			/* remove this case on
			 * assets_as_an_app_v2 cleanup
			 */
			return buildCustomComponentConfig({
				Component: Nav4Assets,
				componentProps,
				elemBefore,
				label,
				isDraggable: false, // Not draggable
			});
		case L1_MENU_ID.CUSTOMERS:
			return fg('berry-6285-customers-l1')
				? buildCustomComponentConfig({
						Component: Customers,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		case L1_MENU_ID.CUSTOMER_SERVICE:
			return buildCustomComponentConfig({
				Component: CustomerService,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.DASHBOARDS:
			return buildCustomComponentConfig({
				Component: Nav4Dashboards,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.FILTERS:
			return buildCustomComponentConfig({
				Component: Nav4Filters,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.GOALS: {
			const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();
			return {
				type: 'link',
				elemBefore,
				label,
				href: USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS],
			};
		}
		case L1_MENU_ID.ITAM:
			return fg('itam_ftw')
				? buildCustomComponentConfig({
						Component: Nav4ITAM,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		case L1_MENU_ID.OPERATIONS:
			return buildCustomComponentConfig({
				Component: Nav4Operations,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.PLANS:
			return buildCustomComponentConfig({
				Component: Nav4Plans,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.PROJECTS:
			return buildCustomComponentConfig({
				Component: Nav4Projects,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.ROADMAPS:
			if (fg('jpd-enable-roadmaps-recents-fe')) {
				return buildCustomComponentConfig({
					Component: Nav4RoadmapsNewWithRecent,
					componentProps,
					elemBefore,
					label,
					isDraggable: true,
				});
			}
			if (fg('jpd-roadmaps-in-jira')) {
				return buildCustomComponentConfig({
					Component: Nav4RoadmapsNew,
					componentProps,
					elemBefore,
					label,
					isDraggable: true,
				});
			}

			return buildCustomComponentConfig({
				Component: Nav4Roadmaps,
				componentProps,
				elemBefore,
				label,
				isDraggable: true,
			});
		case L1_MENU_ID.TEAMS:
			return !isTeamsAppEnabled()
				? buildCustomComponentConfig({
						Component: Teams,
						componentProps,
						elemBefore,
						label,
						isDraggable: true,
					})
				: null;
		default:
			return null;
	}
}

interface CustomComponentConfigParams<TProps extends { isExpanded: boolean }> {
	Component: JSXElementConstructor<Nav4DragAndDropMenuItemProps & TProps>;
	componentProps: TProps;
	elemBefore: ReactNode;
	isDraggable: boolean;
	label: string;
}

function buildCustomComponentConfig<TProps extends { isExpanded: boolean }>(
	params: CustomComponentConfigParams<TProps>,
): CustomItemConfig {
	const { Component, componentProps, elemBefore, isDraggable, label } = params;

	return {
		type: 'custom',
		elemBefore,
		isDraggable: isDraggable && fg('navx-global-side-nav'),
		isMenuListItem: true, // All of these components are already wrapped in MenuListItem
		label,
		Component: (customItemProps: CustomItemProps) => {
			const { isInMoreMenu, visualContentRef } = customItemProps;

			// When the item isInMoreMenu, we cannot render directly as this breaks the Peek logic.
			if (isInMoreMenu) {
				// TODO: We should handle this internally in GSN to avoid duplicating this logic
				return <MoreMenuComponent elemBefore={elemBefore} label={label} {...customItemProps} />;
			}
			const fixVisualContentRef =
				// oxlint-disable-next-line typescript/consistent-type-assertions
				visualContentRef as MutableRefObject<HTMLDivElement | null>;

			return (
				<Component
					{...componentProps}
					{...customItemProps}
					visualContentRef={fixVisualContentRef}
				/>
			);
		},
	};
}

interface MoreMenuCompProps extends CustomItemProps {
	elemBefore: ReactNode;
	label: string;
}

function MoreMenuComponent(props: MoreMenuCompProps) {
	const {
		draggableRef,
		dropIndicator,
		elemBefore,
		hasDragIndicator,
		isDragging,
		label,
		onClick,
		onPeek,
		visualContentRef,
	} = props;
	// This is the same transformation happening inside GSN
	const handleClick = () => {
		onPeek?.();
		onClick?.();
	};

	// Need to coerce this type for ButtonMenuItem
	const fixVisualContentRef =
		// oxlint-disable-next-line typescript/consistent-type-assertions
		visualContentRef as MutableRefObject<HTMLButtonElement | null>;

	return (
		<ButtonMenuItem
			dropIndicator={dropIndicator}
			onClick={handleClick}
			elemBefore={elemBefore}
			hasDragIndicator={hasDragIndicator}
			isDragging={isDragging}
			ref={draggableRef ? mergeRefs(draggableRef, fixVisualContentRef) : fixVisualContentRef}
		>
			{label}
		</ButtonMenuItem>
	);
}
